import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Image,
  Text,
  Input,
  Button,
  useMediaQuery,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  FaLinkedinIn,
  FaTwitter,
  FaInstagram,
  FaFacebookF,
} from "react-icons/fa";
import { HiArrowUpRight, HiArrowRight } from "react-icons/hi2";
import {
  contactLinks,
  headers,
  socialLinks,
  legalLinks,
} from "../components/common/constants";
import Reveal, { EASE } from "../components/common/Reveal";

const iconFor = (icon) => {
  switch (icon) {
    case "linkedin":
      return FaLinkedinIn;
    case "twitter":
      return FaTwitter;
    case "instagram":
      return FaInstagram;
    case "facebook":
      return FaFacebookF;
    default:
      return FaLinkedinIn;
  }
};

// Server-hosted mail proxy (see server/index.js).
const API_URL = process.env.REACT_APP_API_URL?.replace(/\/+$/, "") || "";

// Live "Lagos time" clock that updates every 60s. Purely a texture element,
// makes the footer feel alive.
const useLagosTime = () => {
  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  return time.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Africa/Lagos",
  });
};

const Footer = () => {
  const navigate = useNavigate();
  const [isMobile] = useMediaQuery("(max-width: 1350px)");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();
  const lagosTime = useLagosTime();
  const reduce = useReducedMotion();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/.+@.+\..+/.test(trimmed)) {
      toast({
        title: "Please enter a valid email address.",
        status: "warning",
        duration: 3200,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Server ${res.status}`);
      }
      setEmail("");
      toast({
        title: "You're on the list.",
        description: "We'll email you with FX insights every month.",
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } catch (err) {
      window.location.href = `mailto:info@transalliancecapital.ng?subject=Newsletter%20signup&body=Please%20add%20me%20to%20your%20newsletter.%20Email%3A%20${encodeURIComponent(
        trimmed
      )}`;
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <Box
      as="footer"
      color="slate.300"
      className="full_width"
      pos="relative"
      overflow="hidden"
      bg="#050C1A"
      pt={{ base: "60px", lg: "90px" }}
      mt={{ base: "80px", lg: "120px" }}
    >
      {/* Ambient glow orbs */}
      <Box
        pos="absolute"
        top="-160px"
        left="-100px"
        w="520px"
        h="520px"
        bg="radial-gradient(circle, rgba(37,99,235,0.35), transparent 70%)"
        filter="blur(60px)"
        pointerEvents="none"
      />
      <Box
        pos="absolute"
        bottom="-240px"
        right="-100px"
        w="620px"
        h="620px"
        bg="radial-gradient(circle, rgba(6,182,212,0.24), transparent 70%)"
        filter="blur(80px)"
        pointerEvents="none"
      />

      <Flex justifyContent="center" pos="relative" px="20px">
        <Box w={isMobile ? "100%" : "1350px"}>
          {/* ─── CTA Card ─── */}
          <Reveal>
            <Box
              pos="relative"
              overflow="hidden"
              borderRadius={{ base: "22px", md: "32px" }}
              p={{ base: "34px 26px", md: "60px 56px" }}
              bg="linear-gradient(135deg, rgba(37,99,235,0.22) 0%, rgba(6,182,212,0.18) 100%)"
              border="1px solid rgba(255,255,255,0.08)"
              mb={{ base: "60px", lg: "80px" }}
            >
              {/* Grid backdrop */}
              <Box className="grid-bg" pos="absolute" inset="0" opacity={0.4} />

              <Flex
                direction={{ base: "column", md: "row" }}
                justify="space-between"
                align={{ base: "flex-start", md: "center" }}
                gap="28px"
                pos="relative"
              >
                <Box maxW="640px">
                  <Text
                    fontSize="12px"
                    fontWeight={700}
                    color="accent.400"
                    letterSpacing="wider"
                    textTransform="uppercase"
                  >
                    Let's build something remarkable
                  </Text>
                  <Text
                    mt="14px"
                    fontFamily="heading"
                    fontSize={{ base: "28px", md: "44px", lg: "52px" }}
                    fontWeight={800}
                    color="white"
                    lineHeight="1.05"
                    letterSpacing="-0.02em"
                  >
                    Ready to move money at the{" "}
                    <Text as="span" className="text-gradient">
                      speed of business?
                    </Text>
                  </Text>
                </Box>
                <Button
                  variant="gold"
                  onClick={() => navigate("/contact-us")}
                  h="56px"
                  px="32px"
                  fontSize="15px"
                  rightIcon={<HiArrowRight />}
                  flexShrink={0}
                >
                  Start a conversation
                </Button>
              </Flex>
            </Box>
          </Reveal>

          {/* ─── Main grid ─── */}
          <Grid
            templateColumns={{
              base: "1fr",
              md: "1.4fr 1fr 1fr",
              lg: "1.6fr 1fr 1fr 1.2fr",
            }}
            gap={{ base: "40px", lg: "60px" }}
          >
            {/* Brand + description + newsletter */}
            <GridItem>
              <Reveal>
                <Image
                  onClick={() => {
                    navigate("/");
                    scrollTop();
                  }}
                  cursor="pointer"
                  src="/assets/logo.png"
                  alt="TransAlliance Capital"
                  w="180px"
                  h="42px"
                  objectFit="contain"
                  filter="brightness(0) invert(1)"
                />
                <Text
                  mt="22px"
                  fontSize="14.5px"
                  lineHeight="1.7"
                  color="slate.400"
                  maxW="380px"
                >
                  A licensed financial services partner delivering FX
                  liquidity, cross-border remittances, and specialised
                  treasury solutions across Africa.
                </Text>

                {/* Compact newsletter */}
                <Box mt="26px">
                  <Text
                    fontSize="11px"
                    fontWeight={700}
                    color="slate.500"
                    letterSpacing="wider"
                    textTransform="uppercase"
                  >
                    Monthly FX insights
                  </Text>
                  <Flex mt="10px" gap="8px" as="form" onSubmit={handleSubscribe}>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      bg="rgba(255,255,255,0.06)"
                      border="1px solid rgba(255,255,255,0.1)"
                      color="white"
                      h="46px"
                      _placeholder={{ color: "slate.500" }}
                      _hover={{ borderColor: "rgba(255,255,255,0.2)" }}
                      _focus={{
                        borderColor: "accent.400",
                        boxShadow: "0 0 0 3px rgba(6,182,212,0.25)",
                      }}
                    />
                    <Button
                      type="submit"
                      h="46px"
                      px="18px"
                      fontSize="14px"
                      isLoading={submitting}
                      loadingText="…"
                    >
                      Join
                    </Button>
                  </Flex>
                </Box>
              </Reveal>
            </GridItem>

            {/* Company */}
            <GridItem>
              <Reveal delay={0.08}>
                <Text
                  fontFamily="heading"
                  color="white"
                  fontWeight={700}
                  fontSize="13px"
                  letterSpacing="wider"
                  textTransform="uppercase"
                >
                  Company
                </Text>
                <Flex mt="18px" flexDir="column" gap="10px">
                  {headers?.slice(0, 5)?.map((item, i) => (
                    <Link key={i} to={item?.path || "/"}>
                      <Text
                        fontSize="14.5px"
                        color="slate.400"
                        transition="all .2s"
                        _hover={{ color: "white", pl: "4px" }}
                      >
                        {item?.name}
                      </Text>
                    </Link>
                  ))}
                </Flex>
              </Reveal>
            </GridItem>

            {/* Services */}
            <GridItem>
              <Reveal delay={0.12}>
                <Text
                  fontFamily="heading"
                  color="white"
                  fontWeight={700}
                  fontSize="13px"
                  letterSpacing="wider"
                  textTransform="uppercase"
                >
                  Services
                </Text>
                <Flex mt="18px" flexDir="column" gap="10px">
                  {[
                    "FX Liquidity",
                    "Foreign Remittance",
                    "Local Remittance",
                    "Specialized Services",
                  ].map((item, i) => (
                    <Text
                      key={i}
                      fontSize="14.5px"
                      color="slate.400"
                      cursor="pointer"
                      transition="all .2s"
                      _hover={{ color: "white", pl: "4px" }}
                      onClick={() => navigate("/services")}
                    >
                      {item}
                    </Text>
                  ))}
                </Flex>
              </Reveal>
            </GridItem>

            {/* Reach us */}
            <GridItem>
              <Reveal delay={0.16}>
                <Text
                  fontFamily="heading"
                  color="white"
                  fontWeight={700}
                  fontSize="13px"
                  letterSpacing="wider"
                  textTransform="uppercase"
                >
                  Reach us
                </Text>
                <Flex mt="18px" flexDir="column" gap="12px">
                  {contactLinks?.map((item, i) => (
                    <Flex key={i} align="flex-start" gap="10px">
                      <Image
                        src={item?.img}
                        alt=""
                        aria-hidden="true"
                        w="14px"
                        h="14px"
                        objectFit="contain"
                        filter="brightness(0) invert(1)"
                        opacity={0.55}
                        mt="4px"
                      />
                      <Text
                        fontSize="14px"
                        color="slate.400"
                        lineHeight="1.5"
                        transition="color .2s"
                        _hover={{ color: "white" }}
                      >
                        {item?.url ? (
                          <a href={item?.url}>{item?.title}</a>
                        ) : (
                          item?.title
                        )}
                      </Text>
                    </Flex>
                  ))}
                </Flex>

                {/* Socials */}
                <Flex mt="22px" gap="8px">
                  {socialLinks.map((s, i) => {
                    const Icon = iconFor(s.icon);
                    return (
                      <Flex
                        key={i}
                        as="a"
                        href={s.url || "#"}
                        target={s.url ? "_blank" : undefined}
                        rel={s.url ? "noopener noreferrer" : undefined}
                        aria-label={s.name}
                        w="38px"
                        h="38px"
                        align="center"
                        justify="center"
                        borderRadius="10px"
                        bg="rgba(255,255,255,0.06)"
                        border="1px solid rgba(255,255,255,0.08)"
                        color="slate.300"
                        transition="all .25s cubic-bezier(.22,1,.36,1)"
                        _hover={{
                          bg: "brand.500",
                          color: "white",
                          transform: "translateY(-3px)",
                          borderColor: "transparent",
                        }}
                      >
                        <Icon size="14px" />
                      </Flex>
                    );
                  })}
                </Flex>
              </Reveal>
            </GridItem>
          </Grid>

          {/* ─── Kinetic wordmark ─── */}
          <Box mt={{ base: "60px", lg: "90px" }} pos="relative" pointerEvents="none">
            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 40 }}
              whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1.2, ease: EASE }}
            >
              <Text
                as="div"
                fontFamily="heading"
                fontWeight={800}
                fontSize={{ base: "50px", sm: "80px", md: "120px", lg: "170px" }}
                lineHeight="0.85"
                letterSpacing="-0.05em"
                textAlign="center"
                whiteSpace="nowrap"
                background="linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.02) 100%)"
                sx={{
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
                userSelect="none"
              >
                TRANSALLIANCE
              </Text>
            </motion.div>

            {/* Faint status bar */}
            <Flex
              mt={{ base: "-20px", md: "-40px", lg: "-60px" }}
              justify="center"
              pointerEvents="auto"
            >
              <Flex
                align="center"
                gap="10px"
                px="14px"
                py="8px"
                borderRadius="999px"
                bg="rgba(255,255,255,0.04)"
                border="1px solid rgba(255,255,255,0.08)"
                fontSize="12px"
                color="slate.400"
                backdropFilter="blur(8px)"
              >
                <Box
                  w="7px"
                  h="7px"
                  borderRadius="50%"
                  bg="green.400"
                  className="pulse-ring"
                />
                <Text fontWeight={600} color="slate.300">
                  All systems operational
                </Text>
                <Box w="4px" h="4px" borderRadius="50%" bg="slate.700" />
                <Text>
                  Lagos {lagosTime} <Text as="span" color="slate.500">WAT</Text>
                </Text>
              </Flex>
            </Flex>
          </Box>

          {/* ─── Bottom bar ─── */}
          <Box mt={{ base: "40px", lg: "56px" }} py="24px" borderTop="1px solid rgba(255,255,255,0.06)">
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              gap="14px"
            >
              <Flex align="center" gap="10px" fontSize="13px" color="slate.500">
                <Text>
                  © {new Date().getFullYear()} TransAlliance Capital.
                </Text>
                <Box display={{ base: "none", md: "inline" }} w="4px" h="4px" borderRadius="50%" bg="slate.700" />
                <Text display={{ base: "none", md: "inline" }}>All rights reserved.</Text>
              </Flex>

              <Flex align="center" gap="6px" fontSize="13px" color="slate.500" wrap="wrap">
                {legalLinks.map((l, i) => (
                  <React.Fragment key={l.path}>
                    <Link to={l.path}>
                      <Text
                        transition="color .2s"
                        _hover={{ color: "white" }}
                      >
                        {l.name}
                      </Text>
                    </Link>
                    {i < legalLinks.length - 1 && (
                      <Box w="4px" h="4px" borderRadius="50%" bg="slate.700" />
                    )}
                  </React.Fragment>
                ))}

                <Box w="4px" h="4px" borderRadius="50%" bg="slate.700" mx="4px" />

                <Flex
                  as="button"
                  onClick={scrollTop}
                  align="center"
                  gap="4px"
                  color="slate.500"
                  transition="color .2s"
                  _hover={{ color: "white" }}
                >
                  Back to top
                  <HiArrowUpRight size="12px" />
                </Flex>
              </Flex>
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default Footer;
